import React from 'react';
import { Card, Empty, Skeleton } from 'antd';
import { CheckCircleOutlined, RightOutlined } from '@ant-design/icons';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { useQuery } from '@apollo/client';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import { getComplianceOverviewQuery } from '@src/util/query/compliance.query';
import { getAppraisalOverviewQuery } from '@src/util/query/appraisal.query';
import { getSurveyOverviewQuery } from '@src/util/query/survey.query';

/**
 * The Compliance dashboard (switched to from the Operations one): documents & acknowledgements,
 * appraisals and surveys - each part only when the tenant has the module and the role can see
 * it - and, on the right, what needs attention.
 */
export const ComplianceDashboard = ({ showCompliance, showAppraisals, showSurveys }) => {
  const navigate = useNavigate();
  const { data: complianceData, loading: complianceLoading } = useQuery(getComplianceOverviewQuery, { skip: !showCompliance, fetchPolicy: 'cache-and-network' });
  const { data: appraisalData, loading: appraisalLoading } = useQuery(getAppraisalOverviewQuery, { skip: !showAppraisals, fetchPolicy: 'cache-and-network' });
  const { data: surveyData, loading: surveyLoading } = useQuery(getSurveyOverviewQuery, { skip: !showSurveys, fetchPolicy: 'cache-and-network' });
  const compliance = complianceData?.getComplianceOverview?.response;
  const docs = compliance?.totals;
  const appraisals = appraisalData?.getAppraisalOverview?.response;
  const surveys = surveyData?.getSurveyOverview?.response;

  const tile = (label, value, { to, alert = false, hint } = {}) => (
    <Card key={label} className={`metric-card tile-clickable${alert ? ' alert-card' : ''}`} size="small" onClick={() => navigate(to)} data-hint={hint}>
      <div className="metric-header"><span className="metric-label">{label}</span></div>
      <div className="metric-value">{value}</div>
    </Card>
  );
  const head = (title, to, go) => (
    <div className="dash-section-head">
      <span className="dash-section-title">{title}</span>
      <span className="dash-section-link" onClick={() => navigate(to)}>{go} <RightOutlined style={{ fontSize: 10 }} /></span>
    </div>
  );
  const loadingGrid = <div className="metrics-grid">{[1, 2, 3].map((n) => <Card key={n} className="metric-card" size="small"><Skeleton active paragraph={false} /></Card>)}</div>;

  const stages = appraisals ? [
    { stage: 'Self-assessment', count: appraisals.awaitingOfficer || 0 },
    { stage: 'With reviewer', count: appraisals.awaitingReviewer || 0 },
    { stage: 'Officer to sign', count: appraisals.awaitingSignOff || 0 },
  ] : [];
  // sites with something to sign (a document on a site that's since gone has no one to sign it)
  const sites = (compliance?.bySite || []).filter((row) => row.assignments > 0 && row.siteName).slice(0, 6);

  // what needs doing, most urgent first
  const attention = [
    docs?.overdue > 0 && { key: 'ackOverdue', text: `${docs.overdue} ${docs.overdue === 1 ? 'acknowledgement is' : 'acknowledgements are'} overdue`, to: '/user/compliance', urgent: true },
    appraisals?.overdue > 0 && { key: 'aprOverdue', text: `${appraisals.overdue} ${appraisals.overdue === 1 ? 'appraisal is' : 'appraisals are'} overdue`, to: '/user/appraisals', urgent: true },
    appraisals?.unassigned > 0 && { key: 'aprNoReviewer', text: `${appraisals.unassigned} ${appraisals.unassigned === 1 ? 'appraisal has' : 'appraisals have'} no reviewer`, to: '/user/appraisals', urgent: true },
    ...(compliance?.reviewDue || []).map((doc) => {
      const late = doc.reviewDate && dayjs(doc.reviewDate).isBefore(dayjs(), 'day');
      return { key: `review-${doc._id}`, text: `${late ? 'Review overdue' : 'Review due'}: ${doc.title}`, sub: [doc.appliesTo?.name, doc.reviewDate ? `review by ${dayjs(doc.reviewDate).format('DD/MM/YYYY')}` : null].filter(Boolean).join(' · '), to: '/user/compliance', urgent: late };
    }),
    docs?.expiring > 0 && { key: 'expiring', text: `${docs.expiring} ${docs.expiring === 1 ? 'document expires' : 'documents expire'} within 30 days`, to: '/user/compliance' },
    docs?.outstanding > 0 && { key: 'outstanding', text: `${docs.outstanding} ${docs.outstanding === 1 ? 'document is' : 'documents are'} still to be signed`, to: '/user/compliance' },
    appraisals?.dueNext30Days > 0 && { key: 'aprDue', text: `${appraisals.dueNext30Days} ${appraisals.dueNext30Days === 1 ? 'appraisal is' : 'appraisals are'} due in the next 30 days`, to: '/user/appraisals' },
    surveys?.awaiting > 0 && { key: 'svyAwaiting', text: `${surveys.awaiting} survey ${surveys.awaiting === 1 ? 'answer is' : 'answers are'} still awaited`, to: '/user/surveys' },
  ].filter(Boolean).sort((x, y) => (y.urgent ? 1 : 0) - (x.urgent ? 1 : 0));
  const loadingAny = (showCompliance && complianceLoading && !compliance) || (showAppraisals && appraisalLoading && !appraisals) || (showSurveys && surveyLoading && !surveys);

  return (
    <div className="dashboard-content compliance-dashboard">
      <div className="left-panel">
        {showCompliance && (
          <div className="dash-section" data-section="documents">
            {head('Documents & acknowledgements', '/user/compliance', 'Open Compliance')}
            {!docs ? loadingGrid : (
              <div className="metrics-grid">
                {tile('Compliance complete', `${docs.completion}%`, { to: '/user/compliance', hint: 'Open Compliance' })}
                {tile('Documents outstanding', docs.outstanding, { to: '/user/compliance', hint: 'Open Compliance' })}
                {tile('Overdue acknowledgements', docs.overdue, { to: '/user/compliance', alert: docs.overdue > 0, hint: 'Open Compliance' })}
                {tile('Documents due for review', docs.reviewDue, { to: '/user/compliance', alert: docs.reviewDue > 0, hint: 'Open Compliance' })}
                {tile('Expiring in 30 days', docs.expiring, { to: '/user/compliance', alert: docs.expiring > 0, hint: 'Open Compliance' })}
                {tile('Live documents', docs.documents, { to: '/user/compliance', hint: 'Open Compliance' })}
              </div>
            )}
            {docs && (
              <Card className="chart-card" size="small">
                <div className="chart-header"><span className="chart-title">Signed by site</span><span className="dash-chart-note">lowest first</span></div>
                {sites.length ? (
                  <div className="dash-site-list">
                    {sites.map((row) => (
                      <div key={row.siteId} className="dash-site-row" onClick={() => navigate('/user/compliance')}>
                        <span className="dash-site-name" title={row.siteName}>{row.siteName || 'Site'}</span>
                        <span className="dash-site-bar"><span style={{ width: `${row.completion}%` }} className={row.completion < 50 ? 'low' : row.completion < 100 ? 'mid' : 'full'} /></span>
                        <span className="dash-site-figure">{row.completion}%</span>
                        <span className="dash-site-sub">{row.acknowledged} of {row.assignments} signed{row.overdue ? <b> · {row.overdue} overdue</b> : ''}</span>
                      </div>
                    ))}
                  </div>
                ) : <Empty description="No site documents yet." image={Empty.PRESENTED_IMAGE_SIMPLE} />}
              </Card>
            )}
          </div>
        )}

        {showAppraisals && (
          <div className="dash-section" data-section="appraisals">
            {head('Appraisals', '/user/appraisals', 'Open Appraisals')}
            {!appraisals ? loadingGrid : (
              <div className="metrics-grid">
                {tile('Appraisals open', appraisals.open, { to: '/user/appraisals', hint: 'Open Appraisals' })}
                {tile('Appraisals overdue', appraisals.overdue, { to: '/user/appraisals', alert: appraisals.overdue > 0, hint: 'Open Appraisals' })}
                {tile('Due in 30 days', appraisals.dueNext30Days, { to: '/user/appraisals', hint: 'Open Appraisals' })}
                {tile('Without a reviewer', appraisals.unassigned, { to: '/user/appraisals', alert: appraisals.unassigned > 0, hint: 'Open Appraisals' })}
                {tile('Completed this year', appraisals.completedThisYear, { to: '/user/appraisals', hint: 'Open Appraisals' })}
                {tile('Average score', appraisals.averageScorePercent != null ? `${appraisals.averageScorePercent}%` : '-', { to: '/user/appraisals', hint: 'Open Appraisals' })}
              </div>
            )}
            {appraisals && appraisals.open > 0 && (
              <Card className="chart-card" size="small">
                <div className="chart-header"><span className="chart-title">Open appraisals by stage</span></div>
                <ResponsiveContainer width="100%" height={180}>
                  <BarChart data={stages} layout="vertical" margin={{ left: 20, right: 20 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e8e8e8" horizontal={false} />
                    <XAxis type="number" allowDecimals={false} tick={{ fill: '#8c8c8c', fontSize: 11 }} />
                    <YAxis type="category" dataKey="stage" width={110} tick={{ fill: '#595959', fontSize: 12 }} />
                    <Tooltip formatter={(value) => [value, 'Appraisals']} contentStyle={{ background: '#fff', border: '1px solid #d9d9d9', borderRadius: '4px', fontSize: '12px' }} />
                    <Bar dataKey="count" fill="#17a2b8" radius={[0, 4, 4, 0]} barSize={22} />
                  </BarChart>
                </ResponsiveContainer>
              </Card>
            )}
          </div>
        )}

        {showSurveys && (
          <div className="dash-section" data-section="surveys">
            {head('Surveys', '/user/surveys', 'Open Surveys')}
            {!surveys ? loadingGrid : (
              <div className="metrics-grid">
                {tile('Surveys open', surveys.open, { to: '/user/surveys', hint: 'Open Surveys' })}
                {tile('Answers awaited', surveys.awaiting, { to: '/user/surveys', hint: 'Open Surveys' })}
                {tile('Response rate', surveys.responseRate != null ? `${surveys.responseRate}%` : '-', { to: '/user/surveys', hint: 'Open Surveys' })}
              </div>
            )}
          </div>
        )}
      </div>

      <div className="right-panel">
        <Card className="activity-card dash-attention" size="small">
          <div className="chart-header"><span className="chart-title">Needs attention</span></div>
          {loadingAny ? <Skeleton active /> : attention.length ? (
            <div className="dash-attention-list">
              {attention.map((item) => (
                <div key={item.key} className={`dash-attention-item${item.urgent ? ' urgent' : ''}`} onClick={() => navigate(item.to)}>
                  <span className="dash-attention-dot" />
                  <span className="dash-attention-text">
                    {item.text}
                    {item.sub && <span className="dash-attention-sub">{item.sub}</span>}
                  </span>
                  <RightOutlined className="dash-attention-go" />
                </div>
              ))}
            </div>
          ) : (
            <div className="dash-attention-clear"><CheckCircleOutlined /> Nothing needs attention right now.</div>
          )}
        </Card>
      </div>
    </div>
  );
};
